import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import { Switch, CircularProgress, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowReloadHorizontalIcon } from "@hugeicons-pro/core-stroke-rounded";
import useCalculateDistanceBetweenLocations from "hooks/useCalculateDistanceBetweenLocations";
import { useAllocationSelectOptions } from "hooks/useAllocationSelectOptions";
import ConfigureAddress from "components/common/ConfigureAddress";
import { MileageExpense } from "utils/reimbursements/reimbursementExpenseFormat";
import { FormikAddress } from "utils/addressFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { Select, TextField } from "@outpave/ds";

interface MileageReimbursementFormProps {
  formikCreateExpense: FormikProps<MileageExpense>;
}

/**
 * Employee self-service port of the admin `MileageExpenseForm`. The admin
 * "Pay to" `EmployeeSearchAutoComplete` is intentionally omitted: the payee is
 * always the logged-in user, locked by the parent drawer. The distance / rate
 * calculation logic is identical to the admin form; only the Tailwind wrappers
 * are replaced with DS `Stack` / `Inline` primitives.
 */
const MileageReimbursementForm: React.FC<MileageReimbursementFormProps> = ({
  formikCreateExpense,
}) => {
  const { t } = useTranslation();
  const { handleFormikHelperText } = useFormikHelpers(formikCreateExpense);
  const { calculateDistance, loading: loadingDistance } =
    useCalculateDistanceBetweenLocations();

  // Seeded with the saved allocation so the field renders its own value before
  // any search runs; the DS resolves a label only from `options`.
  const allocation = useAllocationSelectOptions(
    formikCreateExpense.values.allocationCode?.id
      ? {
          id: formikCreateExpense.values.allocationCode.id,
          allocationCode:
            formikCreateExpense.values.allocationCode.allocationCode,
          allocationTypeName: formikCreateExpense.values.allocationType?.name,
        }
      : null,
  );

  const clearAllocation = () => {
    formikCreateExpense.setFieldValue("allocationCode", {
      id: "",
      allocationCode: "",
    });
    formikCreateExpense.setFieldValue("allocationType", { key: "", name: "" });
  };
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(
    null,
  );
  const [overwrittenDistance, setOverwrittenDistance] = useState<number | null>(
    formikCreateExpense.values.isRoundTrip
      ? formikCreateExpense.values.distance / 2
      : formikCreateExpense.values.distance,
  );

  const isDistanceOverwritten =
    formikCreateExpense.values.distance !== calculatedDistance &&
    formikCreateExpense.values.distance !== (calculatedDistance || 0) * 2;

  const updateDistance = (distance: number, isRoundTrip: boolean) => {
    const finalDistance = isRoundTrip ? distance * 2 : distance;
    formikCreateExpense.setFieldValue("distance", finalDistance);
  };

  const handleManualDistanceChange = (value: number) => {
    const updatedValue = formikCreateExpense.values.isRoundTrip
      ? value / 2
      : value;

    formikCreateExpense.setFieldValue("distance", value);
    setOverwrittenDistance(updatedValue);
  };

  const handleCheckRoundTrip = (isChecked: boolean) => {
    formikCreateExpense.setFieldValue("isRoundTrip", isChecked);

    if (calculatedDistance !== null && !isDistanceOverwritten) {
      updateDistance(calculatedDistance, isChecked);
    } else if (overwrittenDistance !== null && isDistanceOverwritten) {
      updateDistance(overwrittenDistance, isChecked);
    }
  };

  const handleCalculateDistance = async (
    tripFrom: FormikAddress,
    tripTo: FormikAddress,
  ) => {
    if (!tripFrom?.geocode || !tripTo?.geocode) return;
    const { isRoundTrip } = formikCreateExpense.values;
    try {
      const { distance, polyline } = await calculateDistance(
        tripFrom.geocode,
        tripTo.geocode,
      );
      setCalculatedDistance(distance);
      formikCreateExpense.setFieldValue("polyline", polyline);

      if (!isDistanceOverwritten) {
        updateDistance(distance, isRoundTrip);
      }
    } catch (error) {
      console.error("Error calculating distance:", error);
    }
  };

  useEffect(() => {
    const { tripFrom, tripTo } = formikCreateExpense.values;
    if (tripFrom?.geocode && tripTo?.geocode) {
      handleCalculateDistance(tripFrom, tripTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikCreateExpense.values?.tripFrom?.geocode,
    formikCreateExpense.values?.tripTo?.geocode,
  ]);

  useEffect(() => {
    if (
      formikCreateExpense.values.distance &&
      formikCreateExpense.values.rate
    ) {
      const amount =
        formikCreateExpense.values.distance * formikCreateExpense.values.rate;
      formikCreateExpense.setFieldValue(
        "amount",
        Math.round(amount * 100) / 100,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikCreateExpense.values.distance, formikCreateExpense.values.rate]);

  return (
    <Stack gap="var(--space-8)" sx={{ width: "100%" }}>
      <ConfigureAddress
        formik={formikCreateExpense}
        textFieldData={{
          name: "tripFrom.addressName",
          label: "From",
          required: true,
          parent: "tripFrom",
          enforceValidAddress: true,
        }}
      />

      <ConfigureAddress
        formik={formikCreateExpense}
        textFieldData={{
          name: "tripTo.addressName",
          label: "To",
          required: true,
          parent: "tripTo",
          enforceValidAddress: true,
        }}
      />

      {/* Round Trip */}
      <Inline justify="between" align="center">
        <Inline gap="var(--space-4)" align="center">
          <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={20} />
          <Typography variant="body2">{t("Round Trip")}</Typography>
        </Inline>
        <Switch
          name="isRoundTrip"
          checked={formikCreateExpense.values.isRoundTrip}
          onChange={(e) => handleCheckRoundTrip(e.target.checked)}
          color="primary"
        />
      </Inline>

      {/* Distance */}
      <TextField
        fullWidth
        type="number"
        name="distance"
        required
        label={
          calculatedDistance !== null
            ? t("EstimatedCalculatedDistance", {
                calculated: formikCreateExpense.values.isRoundTrip
                  ? calculatedDistance * 2
                  : calculatedDistance,
              })
            : t("Distance")
        }
        value={formikCreateExpense.values.distance || ""}
        onChange={(e) => {
          handleManualDistanceChange(parseFloat(e.target.value) || 0);
        }}
        onBlur={formikCreateExpense.handleBlur}
        inputProps={{ min: 0 }}
        trailing={
          loadingDistance ? (
            <CircularProgress color="primary" size="1.5rem" />
          ) : (
            t("miles")
          )
        }
        errorText={handleFormikHelperText("distance") || undefined}
      />

      <Select
        label={t("Allocation")}
        options={allocation.options}
        loading={allocation.loading}
        onInputChange={allocation.onInputChange}
        onScrollEnd={allocation.onScrollEnd}
        noOptionsText={allocation.noOptionsText}
        bottomAction={allocation.bottomAction}
        value={formikCreateExpense.values.allocationCode?.id || null}
        onChange={(id) => {
          if (!id) {
            clearAllocation();
            // The DS clear fires onChange only, so the list would stay filtered.
            allocation.reset();
            return;
          }
          const picked = allocation.allocationById(id);
          formikCreateExpense.setFieldValue("allocationCode", {
            id,
            allocationCode: picked?.allocationCode ?? "",
          });
          formikCreateExpense.setFieldValue("allocationType", {
            key: "",
            name: picked?.allocationTypeName ?? "",
          });
        }}
        placeholder={t("Search allocations")}
        errorText={handleFormikHelperText("allocationCode") || undefined}
        fullWidth
      />

      {/* Description */}
      <TextField
        fullWidth
        name="description"
        label={t("Description")}
        required
        value={formikCreateExpense.values.description}
        onChange={formikCreateExpense.handleChange}
        onBlur={formikCreateExpense.handleBlur}
        placeholder={t("Business Purpose for mileage reimbursement")}
        errorText={handleFormikHelperText("description") || undefined}
      />
    </Stack>
  );
};

export default MileageReimbursementForm;
